import type { ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { MarkSpinner } from "./MarkLoader";
import { cn } from "./cn";

export type Step = { label: ReactNode; state: "done" | "active" | "todo" };

const iconMotion = {
  initial: { opacity: 0, scale: 0.2, filter: "blur(4px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, scale: 0.2, filter: "blur(4px)" },
  transition: { type: "spring" as const, stiffness: 420, damping: 16 },
};

function Mark({ state }: { state: Step["state"] }) {
  return (
    <span className="relative z-10 grid size-7 shrink-0 place-items-center" aria-hidden>
      <AnimatePresence initial={false} mode="popLayout">
        {state === "done" ? (
          <motion.span key="done" {...iconMotion} className="grid size-6 place-items-center rounded-control bg-ok text-white">
            <svg viewBox="0 0 16 16" className="size-4" fill="none">
              <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.span>
        ) : state === "active" ? (
          <motion.span key="active" {...iconMotion} className="grid size-6 place-items-center">
            <MarkSpinner size={20} />
          </motion.span>
        ) : (
          <motion.span key="todo" {...iconMotion} className="size-5 rounded-chip border-2 border-ink/15 bg-page" />
        )}
      </AnimatePresence>
    </span>
  );
}

export function Steps({ steps, className }: { steps: Step[]; className?: string }) {
  const active = steps.find((s) => s.state === "active");
  return (
    <div className={className}>
      <ol className="flex flex-col">
        {steps.map((s, i) => (
          <li key={i} className={cn("relative flex items-center gap-3.5", i < steps.length - 1 && "pb-4")}>
            {i < steps.length - 1 ? (
              <span aria-hidden className="absolute left-[13px] top-7 bottom-0 w-0.5 overflow-hidden bg-ink/10">
                <motion.span
                  className="block size-full origin-top bg-ok"
                  initial={false}
                  animate={{ scaleY: s.state === "done" ? 1 : 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 26 }}
                />
              </span>
            ) : null}
            <Mark state={s.state} />
            <span
              className={cn(
                "min-w-0 text-[15px] transition-[color] duration-200",
                s.state === "todo" ? "text-muted" : "text-ink",
                s.state === "active" && "font-semibold",
              )}
            >
              {s.label}
              <span className="sr-only">{s.state === "done" ? ", done" : s.state === "active" ? ", in progress" : ""}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="sr-only" aria-live="polite">
        {active ? active.label : null}
      </p>
    </div>
  );
}
